import { error, exception } from '@/utils/logging'
import { jQueryAjaxCall } from '@/utils/ajaxWrapper'

/**
 * Operation to upsert, delete, or move a line item and its children.
 */
export interface AvettiLineItemOp {
  /**
   * Identifies a line item.
   * The server will automatically generate a `proofId` if one is omitted when a new item is added.
   * However, the client should always generate a `uuid` when inserting a personalized item.
   */
  uuid?: string
  /**
   * Required on insert. May update an existing line item.
   */
  itemId?: number
  /**
   * Sets an absolute quantity. If this is omitted, the existing quantity is incremented by 1.
   */
  quantity?: number
  /**
   * Set to true to save this line item for later, or move an existing line item from the cart to saved for later.
   */
  savedForLater?: boolean

  /**
   * Deletes the line item.
   */
  _destroy?: boolean
}

const buildData = (items: AvettiLineItemOp[]) => {
  const opNameToApiName: { [opName: string]: string } = {
    _destroy: 'removal',
    uuid: 'proofId',
    savedForLater: 'toSaveForLater'
  }

  return items.reduce(
    (parsedData, item, index) => {
      // apply defaults to each basketItem
      parsedData[`basketItems[${index}].itemToProcess`] = true
      parsedData[`basketItems[${index}].vendorId`] = 20090115001
      Object.entries(item).forEach(([opName, value]) => {
        if (value !== undefined) {
          const apiName = opNameToApiName[opName] || opName
          parsedData[`basketItems[${index}].${apiName}`] = value
        }
      })
      return parsedData
    },
    {} as { [key: string]: string | number | boolean }
  )
}

const validateUniqueUuids = (avettiLineItemOps: AvettiLineItemOp[]) => {
  const allUuids = avettiLineItemOps.map((item) => item.uuid)

  const duplicate = allUuids.find(
    (uuid, index) => allUuids.lastIndexOf(uuid) !== index
  )

  if (duplicate) {
    error('Duplicate UUIDs passed to Avetti cart.update()', {
      uuid: duplicate,
      items: avettiLineItemOps
    })
    throw new Error('Duplicate UUIDs found')
  }
}

export async function update(data: AvettiLineItemOp[]): Promise<void> {
  validateUniqueUuids(data)

  const response = await jQueryAjaxCall<AvettiResponse<AvettiCartResponse>>({
    url: '/cards/addItemToCart.html',
    type: 'POST',
    data: {
      vid: 20090115001,
      addItem: 'addItem',
      mode: 'addItem',
      ...buildData(data)
    },
    authenticate: true
  })
  if (typeof response !== 'object') {
    throw new Error(`There was an error updating the cart.`)
  } else if (!response.success) {
    const errorMessages =
      typeof response === 'object' && response.globalErrors !== undefined
        ? ': ' + response.globalErrors.join(', ')
        : ''
    throw new Error(`There was an error updating the cart. ${errorMessages}`)
  } else if (!response.result) {
    throw new Error('There was an error updating the cart.')
  } else if (response?.success !== true) {
    const errorMessages = response?.globalErrors?.join(', ')
    throw new Error(`There was an error updating the cart ${errorMessages}`)
  }
}

export interface AvettiCartItem {
  title: string
  formatName: string
  proofId: string
  quantityShipped: number
  quantityMailed: number
  itemId: number
  itemType: string
  catalogImage: string
  mailDate: string
  shipDate: string
  totalPrice: number
  compCode: string
  onlyToSelf: boolean
  personalize: boolean
  addressListUsed: boolean
  addressListShipped: boolean
  minQuantity: number
  maxQuantity: number
  qtyUnits: number
  qtyList: { [key: string]: string }
  children?: HierarchyCartItem[]
  created: string
}

export interface HierarchyCartItem extends AvettiCartItem {
  projectId: string
}

export interface AvettiResponse<T> {
  success: boolean
  globalErrors?: string[]
  result?: T
  message?: string
}

export interface AvettiCartResponse {
  loggedIn: boolean
  firstName: string
  lastName: string
  email: string
  token: string
  cart: AvettiCartItem[]
  savedForLater: AvettiCartItem[]
}

/**
 * Cart cache stores the promise which will returns the cart and saved for later items when a call
 * is made in this way we avoid multiple api calls for get the same information.
 */
let cartResponseCache: Promise<AvettiResponse<AvettiCartResponse>> | null =
  window.cartData ? Promise.resolve(window.cartData) : null

/**
 * Clears the cart and saved for later cache before any update, allowing us to get correct data from backend
 * after any change.
 */
export function clearCartCache(): void {
  cartResponseCache = null
}

/**
 * Retrieves the cart and saved for later items.
 */
async function getAvettiCartAndSavedForLater(): Promise<AvettiCartResponse> {
  try {
    if (!cartResponseCache) {
      cartResponseCache = jQueryAjaxCall<AvettiResponse<AvettiCartResponse>>({
        url: '/cards/editCartItems.html',
        dataType: 'json',
        data: {
          vid: 20090115001,
          ajax: true
        },
        authenticate: true
      })
    }
    return (await cartResponseCache).result as AvettiCartResponse
  } catch (getCartError) {
    // Don't cache a failed response
    clearCartCache()

    exception(getCartError)
    throw getCartError
  }
}

/**
 * Retrieves the cart portion of the Avetti response.
 */
export async function getAvettiCart(): Promise<AvettiCartItem[]> {
  return (await getAvettiCartAndSavedForLater()).cart
}

/**
 * Retrieves the saved for later portion of the Avetti response.
 */
export async function getAvettiSavedForLater(): Promise<AvettiCartItem[]> {
  return (await getAvettiCartAndSavedForLater()).savedForLater
}

/**
 *  Ensures that quantityMailed is up-to-date.
 *  Calls update cart API without any parameters. Usually we use this API for setting address list flags,
 *  however we can call the same API with minimal parameters to force a cart refresh.
 *  Normally, there's no reason to call this. However, there's an intermittent bug that prevents quantityMailed
 *  from updating consistently for the item with addressListUsed: true. After calling this, quantityMailed
 *  appears to be always correct.
 */
export async function refreshCart(): Promise<void> {
  // TODO: Remove this API once we strip the address book out of the cart.
  await jQueryAjaxCall<AvettiResponse<AvettiCartResponse>>({
    url: '/cards/editCartItems.html',
    type: 'POST',
    data: {
      vid: 20090115001,
      mode: 'update',
      ajax: true
    }
  })
}
